import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleLogin = () => {
    // Keep existing Google auth behaviour
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/auth/login-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const user = await response.json();
        toast.success(t("common_success"));
        navigate("/dashboard", { state: { user } });
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common_error"));
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(t("common_error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-4">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="text-center space-y-2">
          <h1 className="font-heading text-2xl font-semibold text-stone-900">
            {t("auth_login_title")}
          </h1>
          <p className="text-stone-600 text-sm">
            {t("auth_login_subtitle")}
          </p>
        </div>

        <Button
          type="button"
          onClick={handleGoogleLogin}
          className="w-full btn-primary"
          disabled={isLoading}
        >
          {t("auth_login_with_google")}
        </Button>

        <div className="flex items-center gap-2 text-xs text-stone-400">
          <div className="flex-1 h-px bg-stone-200" />
          <span>{t("auth_or")}</span>
          <div className="flex-1 h-px bg-stone-200" />
        </div>

        <form onSubmit={handleEmailLogin} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              {t("auth_email")}
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              {t("auth_password")}
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <Button
            type="submit"
            className="w-full btn-primary"
            disabled={isLoading}
          >
            {isLoading ? t("common_loading") : t("auth_login_button")}
          </Button>
        </form>

        <p className="text-center text-sm text-stone-500">
          {t("auth_no_account")} {" "}
          <Link to="/signup" className="text-primary font-medium">
            {t("auth_signup_link")}
          </Link>
        </p>
      </Card>
    </div>
  );
};

export default LoginPage;
